import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Database, Upload, Trash2, Loader2, FileSpreadsheet, MapPin, AlertTriangle, Search, Pencil, Save, X, Globe, CheckCircle, XCircle, Download, RefreshCw, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { refreshSchengenEuStatus, exportAirportDataXlsx, getUnrecognizedAirportCodes, syncAirportToFlights } from "@/lib/api";
import { usePermissions } from "@/context/PermissionContext";
import DeleteWithBackupDialog from "@/components/DeleteWithBackupDialog";

const CONTINENT_OPTIONS = [
  { value: "all", label: "All Continents" },
  { value: "europe_schengen", label: "Europe (Schengen)" },
  { value: "europe_non_schengen", label: "Europe (Non-Schengen)" },
  { value: "north_america", label: "North America" },
  { value: "south_america", label: "South America" },
  { value: "asia", label: "Asia" },
  { value: "africa", label: "Africa" },
  { value: "oceania", label: "Oceania" },
];
const CONTINENT_NAME_MAP = {
  north_america: "North America",
  south_america: "South America",
  asia: "Asia",
  africa: "Africa",
  oceania: "Oceania",
};

export default function AirportDataPage() {
  const { hasPermission } = usePermissions();
  const [searchCode, setSearchCode] = useState("");
  const [searchName, setSearchName] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowForm, setRowForm] = useState({});
  const [countryFilter, setCountryFilter] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editAirport, setEditAirport] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [schengenFilter, setSchengenFilter] = useState("all"); // "all", "schengen", "non-schengen"
  const [nonEuFilter, setNonEuFilter] = useState("all"); // "all", "eu", "non-eu"
  const [continentFilter, setContinentFilter] = useState("all");
  const [countryFilterOpen, setCountryFilterOpen] = useState(false);
  const [countryHeaderInput, setCountryHeaderInput] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [reviewCodes, setReviewCodes] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const loadReviewCodes = async () => {
    setReviewLoading(true);
    setReviewOpen(true);
    try {
      const res = await getUnrecognizedAirportCodes();
      setReviewCodes(res.data.results || []);
    } catch {
      toast.error("Failed to load movement codes");
    } finally {
      setReviewLoading(false);
    }
  };

  const openFixDialog = (item) => {
    if (item.in_db && item.airport) {
      openEditDialog(item.airport);
    } else {
      setEditAirport({ id: "new" });
      setEditForm({ icao_code: item.code, airport_name: "", country: "", schengen: false, non_eu: false });
    }
  };

  const handleSyncCode = async (code) => {
    try {
      const res = await syncAirportToFlights(code);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
  };
  const PAGE_SIZE = 50;
  const fileInputRef = useRef(null);

  // Permission checks
  const canAdd = hasPermission("airport_data", "add");
  const canEdit = hasPermission("airport_data", "edit");
  const canDelete = hasPermission("airport_data", "delete");
  const canDeleteAll = hasPermission("airport_data", "delete_all");
  const canUpload = hasPermission("airport_data", "upload");
  const canExport = hasPermission("airport_data", "export");

  useEffect(() => {
    fetchTotalCount();
  }, []);

  const fetchTotalCount = async () => {
    try {
      const response = await api.get("/airport-data/count");
      setTotalCount(response.data.count);
    } catch {
      // count endpoint optional
    }
  };

  const handleSearch = () => doSearch(1);

  const applyCountryHeaderFilter = () => {
    const code = countryHeaderInput.trim();
    setCountryFilter(code);
    setCountryFilterOpen(false);
    doSearch(1, { country: code });
  };

  const clearCountryHeaderFilter = () => {
    setCountryHeaderInput("");
    setCountryFilter("");
    setCountryFilterOpen(false);
    doSearch(1, { country: "" });
  };

  const doSearch = async (targetPage = 1, overrides = {}) => {
    const countryVal = (overrides.country !== undefined ? overrides.country : countryFilter).trim();
    if (!searchCode.trim() && !searchName.trim() && !countryVal && schengenFilter === "all" && nonEuFilter === "all" && continentFilter === "all") {
      toast.error("Please enter a search term or select a filter");
      return;
    }

    setSearching(true);
    setHasSearched(true);
    try {
      let params = new URLSearchParams();
      if (searchCode.trim()) {
        params.append("code", searchCode.trim().toUpperCase());
      }
      if (searchName.trim()) {
        params.append("name", searchName.trim());
      }
      if (countryVal) {
        params.append("country", countryVal);
      }
      // Continent filter (Europe split applies the schengen param directly)
      let schengenParamApplied = false;
      if (continentFilter !== "all") {
        if (continentFilter === "europe_schengen") {
          params.append("continent", "Europe");
          params.append("schengen", "true");
          schengenParamApplied = true;
        } else if (continentFilter === "europe_non_schengen") {
          params.append("continent", "Europe");
          params.append("schengen", "false");
          schengenParamApplied = true;
        } else {
          params.append("continent", CONTINENT_NAME_MAP[continentFilter]);
        }
      }
      if (!schengenParamApplied && schengenFilter !== "all") {
        params.append("schengen", schengenFilter === "schengen" ? "true" : "false");
      }
      if (nonEuFilter !== "all") {
        params.append("non_eu", nonEuFilter === "non-eu" ? "true" : "false");
      }
      params.append("page", String(targetPage));
      params.append("page_size", String(PAGE_SIZE));

      const response = await api.get(`/airport-data/search?${params.toString()}`);
      const d = response.data || {};
      setSearchResults(d.results || []);
      setTotalResults(d.total || 0);
      setTotalPages(d.total_pages || 0);
      setPage(d.page || targetPage);
      if ((d.results || []).length === 0) {
        toast.info("No airports found matching your search");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Export all airport data to XLS
  const handleExport = async () => {
    try {
      const response = await exportAirportDataXlsx();
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `airport_data_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      toast.success("Airport data exported");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Refresh Schengen/EU status for all flights with progress polling every 2 seconds
  const handleRefreshFlights = async () => {
    setRefreshing(true);
    setRefreshProgress({ running: true, total: 0, processed: 0, updated: 0, phase: "Starting..." });
    
    // Start polling progress every 2 seconds
    const progressInterval = setInterval(async () => {
      try {
        const prog = await api.get("/refresh-schengen-eu-status/progress");
        setRefreshProgress(prog.data);
        if (!prog.data.running) {
          clearInterval(progressInterval);
        }
      } catch {
        // Ignore polling errors
      }
    }, 2000);
    
    try {
      const response = await refreshSchengenEuStatus();
      const updated = response.data.flights_updated || 0;
      clearInterval(progressInterval);
      setRefreshProgress(null);
      if (updated > 0) {
        toast.success(`Updated Schengen/EU status for ${updated} flights`);
      } else {
        toast.info("All flights already have correct Schengen/EU status");
      }
    } catch (error) {
      clearInterval(progressInterval);
      setRefreshProgress(null);
      toast.error(error.response?.data?.detail || "Failed to refresh flight status");
    } finally {
      setRefreshing(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      toast.error("Please select an XLS or XLSX file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/airport-data/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      fetchTotalCount();
      setSearchResults([]);
      setHasSearched(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/airport-data/${id}`);
      toast.success("Airport deleted");
      setDeleteId(null);
      setSearchResults(prev => prev.filter(a => a.id !== id));
      fetchTotalCount();
    } catch (error) {
      toast.error("Failed to delete airport");
    }
  };

  const handleDeleteAll = async () => {
    try {
      await api.delete("/airport-data");
      toast.success("All airport data deleted");
      setSearchResults([]);
      setTotalCount(0);
    } catch (error) {
      toast.error("Failed to delete data");
      throw error; // Re-throw for the dialog to handle
    }
  };

  const openEditDialog = (airport) => {
    setEditAirport(airport);
    setEditForm({
      icao_code: airport.icao_code,
      airport_name: airport.airport_name || "",
      country: airport.country || "",
      schengen: airport.schengen || false,
      non_eu: airport.non_eu || false
    });
  };

  const startInlineEdit = (airport) => {
    setEditingId(airport.id);
    setRowForm({
      icao_code: airport.icao_code || "",
      airport_name: airport.airport_name || "",
      country: airport.country || "",
      schengen: airport.schengen || false,
      non_eu: airport.non_eu || false,
    });
  };

  const cancelInlineEdit = () => {
    setEditingId(null);
    setRowForm({});
  };

  const saveInlineEdit = async (airport) => {
    setSaving(true);
    try {
      const payload = { ...rowForm, icao_code: (rowForm.icao_code || "").toUpperCase(), country: (rowForm.country || "").toUpperCase() };
      const res = await api.put(`/airport-data/${airport.id}`, payload);
      toast.success(res.data?.message || "Airport updated & related flights synced");
      setSearchResults((prev) => prev.map((a) => (a.id === airport.id ? { ...a, ...payload, country_name: undefined } : a)));
      setEditingId(null);
      setRowForm({});
      if (reviewOpen) loadReviewCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save airport");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editAirport) return;
    
    setSaving(true);
    try {
      if (editAirport.id === "new") {
        // Adding new airport
        if (!editForm.icao_code) {
          toast.error("ICAO code is required");
          setSaving(false);
          return;
        }
        await api.post("/airport-data", editForm);
        // Propagate the new airport info to any related flights
        try {
          const syncRes = await syncAirportToFlights(editForm.icao_code);
          toast.success(`Airport added. ${syncRes.data.flights_updated} flight leg(s) synced.`);
        } catch {
          toast.success("Airport added successfully");
        }
        fetchTotalCount();
      } else {
        // Updating existing airport (PUT already propagates to flights)
        await api.put(`/airport-data/${editAirport.id}`, editForm);
        toast.success("Airport updated & related flights synced");
        setSearchResults(prev => prev.map(a => 
          a.id === editAirport.id ? { ...a, ...editForm } : a
        ));
      }
      setEditAirport(null);
      if (reviewOpen) loadReviewCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save airport");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="airport-data-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] font-['Outfit']">Airport Data</h1>
          <p className="text-[#64748b] mt-1">Manage airport ICAO codes and Schengen status</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={loadReviewCodes}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="review-codes-btn"
          >
            <AlertTriangle size={16} className="mr-2" />
            Review Movement Codes
          </Button>
          {canAdd && (
            <Button
              onClick={() => {
                setEditAirport({ id: "new" });
                setEditForm({ icao_code: "", airport_name: "", country: "", schengen: false, non_eu: false });
              }}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-semibold"
              data-testid="add-airport-btn"
            >
              <MapPin size={16} className="mr-2" />
              Add Airport
            </Button>
          )}
          <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b] text-lg px-4 py-2">
            {totalCount.toLocaleString()} Airports
          </Badge>
        </div>
      </div>

      {/* Movement codes to review */}
      {reviewOpen && (
        <Card className="border-amber-200 bg-amber-50/40 mb-6" data-testid="review-codes-panel">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between font-['Outfit'] text-[#0f172a] text-lg">
              <span className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-amber-600" />
                Codes from movements needing attention
                {!reviewLoading && (
                  <Badge className="bg-amber-100 text-amber-700">{reviewCodes.length}</Badge>
                )}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={loadReviewCodes} data-testid="review-refresh-btn">
                  <RefreshCw size={14} className={reviewLoading ? "animate-spin" : ""} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setReviewOpen(false)}>
                  <X size={16} />
                </Button>
              </div>
            </CardTitle>
            <CardDescription>
              Airport codes used in flights that are missing from the database or have incomplete info.
              Fix a code to add/correct it, then flights are synced automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reviewLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
              </div>
            ) : reviewCodes.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-700 py-4">
                <CheckCircle size={18} /> All movement codes are recognized and complete.
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 overflow-hidden bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ICAO</TableHead>
                      <TableHead>Flights</TableHead>
                      <TableHead>Issue</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewCodes.map((item) => (
                      <TableRow key={item.code} data-testid={`review-row-${item.code}`}>
                        <TableCell className="font-mono font-semibold">{item.code}</TableCell>
                        <TableCell>{item.count}</TableCell>
                        <TableCell>
                          {item.in_db ? (
                            <Badge className="bg-orange-100 text-orange-700">Missing {item.missing.join(", ")}</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Not in database</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canAdd && (
                              <Button size="sm" variant="outline" onClick={() => openFixDialog(item)}
                                data-testid={`fix-${item.code}`}>
                                <Pencil size={13} className="mr-1" /> Fix
                              </Button>
                            )}
                            {item.in_db && (
                              <Button size="sm" variant="ghost" onClick={() => handleSyncCode(item.code)}
                                data-testid={`sync-${item.code}`}>
                                <RefreshCw size={13} className="mr-1" /> Sync
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-[#e2e8f0] mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] text-lg">
            <Search size={20} />
            Search Airports
          </CardTitle>
          <CardDescription>
            Search separately by ICAO code, airport name, or country (name or code) — plus filters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-xs text-[#64748b] mb-1 block">ICAO Code</Label>
                <Input
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g., LTFM"
                  className="bg-white border-[#e2e8f0] text-[#0f172a] uppercase"
                  data-testid="airport-search-code"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-[#64748b] mb-1 block">Airport Name</Label>
                <Input
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g., Istanbul"
                  className="bg-white border-[#e2e8f0] text-[#0f172a]"
                  data-testid="airport-search-name"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-[#64748b] mb-1 block">Country (name or code)</Label>
                <Input
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g., Turkey or TR"
                  maxLength={40}
                  className="bg-white border-[#e2e8f0] text-[#0f172a]"
                  data-testid="country-filter-input"
                />
              </div>
              <div className="flex items-end gap-2">
              <Button
                onClick={handleSearch}
                disabled={searching}
                className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
                data-testid="airport-search-btn"
              >
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search size={16} className="mr-2" />
                    Search
                  </>
                )}
              </Button>
              </div>
            </div>
            {/* Continent + Schengen Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-[#64748b] font-medium">Continent:</span>
              <Select value={continentFilter} onValueChange={setContinentFilter}>
                <SelectTrigger className="w-52 h-9 bg-white border-[#e2e8f0]" data-testid="continent-filter">
                  <SelectValue placeholder="All Continents" />
                </SelectTrigger>
                <SelectContent>
                  {CONTINENT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`continent-option-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-[#64748b] font-medium ml-4">Schengen:</span>
              <div className="flex gap-1">
                <Button
                  variant={schengenFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSchengenFilter("all")}
                  className={schengenFilter === "all" ? "bg-[#0f172a] text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-all-btn"
                >
                  <Globe size={14} className="mr-1" />
                  All
                </Button>
                <Button
                  variant={schengenFilter === "schengen" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSchengenFilter("schengen")}
                  className={schengenFilter === "schengen" ? "bg-green-600 text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-schengen-btn"
                >
                  <CheckCircle size={14} className="mr-1" />
                  Schengen
                </Button>
                <Button
                  variant={schengenFilter === "non-schengen" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSchengenFilter("non-schengen")}
                  className={schengenFilter === "non-schengen" ? "bg-red-600 text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-non-schengen-btn"
                >
                  <XCircle size={14} className="mr-1" />
                  Non-Schengen
                </Button>
              </div>
              
              {/* Non-EU Filter */}
              <span className="text-sm text-[#64748b] font-medium ml-4">EU Status:</span>
              <div className="flex gap-1">
                <Button
                  variant={nonEuFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNonEuFilter("all")}
                  className={nonEuFilter === "all" ? "bg-[#0f172a] text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-eu-all-btn"
                >
                  All
                </Button>
                <Button
                  variant={nonEuFilter === "eu" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNonEuFilter("eu")}
                  className={nonEuFilter === "eu" ? "bg-blue-600 text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-eu-btn"
                >
                  EU
                </Button>
                <Button
                  variant={nonEuFilter === "non-eu" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNonEuFilter("non-eu")}
                  className={nonEuFilter === "non-eu" ? "bg-orange-600 text-white" : "border-[#e2e8f0]"}
                  data-testid="filter-non-eu-btn"
                >
                  Non-EU
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="border-[#e2e8f0] mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a] text-lg">
            <FileSpreadsheet size={20} />
            Import Airport Data
          </CardTitle>
          <CardDescription>
            Upload XLS/XLSX file with columns: <strong>Code</strong> (ICAO), <strong>Base/Airport</strong> (Name), <strong>Country</strong>, <strong>Schengen</strong> (X if Schengen)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="airport-file-input"
            />
            {canUpload && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                data-testid="upload-airport-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Upload XLS File
                  </>
                )}
              </Button>
            )}

            {canExport && (
              <Button
                variant="outline"
                onClick={handleExport}
                className="text-[#0f172a] hover:bg-[#f1f5f9] border-[#e2e8f0]"
                data-testid="export-airport-btn"
              >
                <Download size={16} className="mr-2" />
                Export XLS
              </Button>
            )}

            <Button
              onClick={handleRefreshFlights}
              disabled={refreshing}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="refresh-flights-btn"
            >
              <RefreshCw size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing..." : "Refresh All Flights"}
            </Button>

            {/* Progress indicator when refreshing */}
            {refreshing && refreshProgress && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="refresh-progress">
                <Loader2 size={14} className="animate-spin text-blue-600" />
                <span className="text-blue-700 font-medium">
                  {refreshProgress.phase} {refreshProgress.total > 0 && `(${refreshProgress.processed}/${refreshProgress.total})`}
                  {refreshProgress.updated > 0 && ` — ${refreshProgress.updated} updated`}
                </span>
              </div>
            )}

            {canDeleteAll && totalCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setDeleteAllOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-all-airport-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete All Data
              </Button>
            )}
          </div>
          
          {/* Refresh explanation */}
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <RefreshCw size={14} />
              <strong>Refresh All Flights:</strong> Updates Schengen/Non-EU status on all flights based on current airport data
            </p>
          </div>
          
          <div className="mt-4 p-3 bg-[#f8fafc] rounded-lg">
            <p className="text-sm text-[#64748b]">
              <strong>Expected columns:</strong>
            </p>
            <ul className="text-xs text-[#94a3b8] mt-1 space-y-1">
              <li>• <strong>Code/ICAO</strong>: 4-letter ICAO airport code (e.g., EBCI, LFPG)</li>
              <li>• <strong>Base/Airport/Name</strong>: Airport name</li>
              <li>• <strong>Country</strong>: Country name</li>
              <li>• <strong>Schengen</strong>: Mark with "X" if in Schengen area, leave empty if not</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Search Results Table */}
      {searchResults.length > 0 && (
        <Card className="border-[#e2e8f0] mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Outfit'] text-[#0f172a]">
              Search Results — {totalResults.toLocaleString()} airport{totalResults === 1 ? "" : "s"}
              {totalPages > 1 && <span className="text-sm font-normal text-[#64748b]"> (page {page} of {totalPages})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#0f172a] hover:bg-[#0f172a]">
                    <TableHead className="text-white font-semibold">ICAO Code</TableHead>
                    <TableHead className="text-white font-semibold">Airport Name</TableHead>
                    <TableHead className="text-white font-semibold">
                      <div className="flex items-center gap-1.5">
                        Country
                        <Popover
                          open={countryFilterOpen}
                          onOpenChange={(o) => { setCountryFilterOpen(o); if (o) setCountryHeaderInput(countryFilter); }}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={`p-1 rounded transition-colors hover:bg-white/15 ${countryFilter ? "text-[#d4af37]" : "text-white/70"}`}
                              title="Filter by country code"
                              data-testid="country-header-filter-btn"
                            >
                              <Filter size={14} fill={countryFilter ? "#d4af37" : "none"} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <Label className="text-xs text-[#64748b]">Filter by country code</Label>
                              <Input
                                autoFocus
                                value={countryHeaderInput}
                                onChange={(e) => setCountryHeaderInput(e.target.value.toUpperCase())}
                                onKeyDown={(e) => { if (e.key === "Enter") applyCountryHeaderFilter(); }}
                                placeholder="e.g., DK, FR, US"
                                maxLength={30}
                                className="h-8 uppercase bg-white border-[#e2e8f0] text-[#0f172a]"
                                data-testid="country-header-filter-input"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="flex-1 h-8 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
                                  onClick={applyCountryHeaderFilter}
                                  data-testid="country-header-filter-apply"
                                >
                                  Apply
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 border-[#e2e8f0] text-[#64748b]"
                                  onClick={clearCountryHeaderFilter}
                                  data-testid="country-header-filter-clear"
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </TableHead>
                    <TableHead className="text-white font-semibold text-center">Schengen</TableHead>
                    <TableHead className="text-white font-semibold text-center">EU Status</TableHead>
                    <TableHead className="text-white font-semibold w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map((airport, index) => (
                    <TableRow 
                      key={airport.id || index} 
                      className="hover:bg-[#f8fafc]"
                      data-testid={`airport-row-${index}`}
                    >
                      <TableCell>
                        {editingId === airport.id ? (
                          <Input
                            value={rowForm.icao_code}
                            onChange={(e) => setRowForm({ ...rowForm, icao_code: e.target.value.toUpperCase() })}
                            className="h-8 font-mono uppercase w-28"
                            data-testid={`inline-code-${airport.id}`}
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#0f172a] rounded flex items-center justify-center">
                              <MapPin size={14} className="text-[#d4af37]" />
                            </div>
                            <span className="font-mono font-semibold text-[#0f172a]">
                              {airport.icao_code}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-[#0f172a]">
                        {editingId === airport.id ? (
                          <Input
                            value={rowForm.airport_name}
                            onChange={(e) => setRowForm({ ...rowForm, airport_name: e.target.value })}
                            className="h-8"
                            data-testid={`inline-name-${airport.id}`}
                          />
                        ) : (airport.airport_name || "-")}
                      </TableCell>
                      <TableCell>
                        {editingId === airport.id ? (
                          <Input
                            value={rowForm.country}
                            onChange={(e) => setRowForm({ ...rowForm, country: e.target.value.toUpperCase() })}
                            className="h-8 uppercase w-24"
                            placeholder="TR"
                            data-testid={`inline-country-${airport.id}`}
                          />
                        ) : (
                          <div className="flex items-center gap-1">
                            <Globe size={14} className="text-[#64748b]" />
                            <span className="text-[#0f172a]">
                              {airport.country_name && airport.country_name !== airport.country
                                ? `${airport.country_name} (${airport.country})`
                                : (airport.country || "-")}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {editingId === airport.id ? (
                          <button
                            type="button"
                            onClick={() => setRowForm({ ...rowForm, schengen: !rowForm.schengen })}
                            data-testid={`inline-schengen-${airport.id}`}
                          >
                            {rowForm.schengen ? (
                              <Badge className="bg-green-500 text-white cursor-pointer"><CheckCircle size={12} className="mr-1" /> Schengen</Badge>
                            ) : (
                              <Badge variant="outline" className="text-red-600 border-red-300 cursor-pointer"><XCircle size={12} className="mr-1" /> Non-Schengen</Badge>
                            )}
                          </button>
                        ) : airport.schengen ? (
                          <Badge className="bg-green-500 text-white">
                            <CheckCircle size={12} className="mr-1" /> Schengen
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-300">
                            <XCircle size={12} className="mr-1" /> Non-Schengen
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {editingId === airport.id ? (
                          <button
                            type="button"
                            onClick={() => setRowForm({ ...rowForm, non_eu: !rowForm.non_eu })}
                            data-testid={`inline-eu-${airport.id}`}
                          >
                            {rowForm.non_eu ? (
                              <Badge className="bg-orange-500 text-white cursor-pointer">Non-EU</Badge>
                            ) : (
                              <Badge className="bg-blue-500 text-white cursor-pointer">EU</Badge>
                            )}
                          </button>
                        ) : airport.non_eu ? (
                          <Badge className="bg-orange-500 text-white animate-pulse">
                            Non-EU
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-500 text-white">
                            EU
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {editingId === airport.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => saveInlineEdit(airport)}
                                disabled={saving}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`save-inline-airport-${airport.id}`}
                              >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={cancelInlineEdit}
                                className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                                data-testid={`cancel-inline-airport-${airport.id}`}
                              >
                                <X size={16} />
                              </Button>
                            </>
                          ) : (
                            <>
                              {canEdit && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => startInlineEdit(airport)}
                                  className="text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]"
                                  data-testid={`edit-airport-${airport.id}`}
                                >
                                  <Pencil size={16} />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteId(airport.id)}
                                  className="text-[#64748b] hover:text-red-600 hover:bg-red-50"
                                  data-testid={`delete-airport-${airport.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#e2e8f0]" data-testid="airport-pagination">
                <span className="text-sm text-[#64748b]">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalResults)} of {totalResults.toLocaleString()}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || searching}
                    onClick={() => doSearch(page - 1)}
                    className="border-[#e2e8f0]"
                    data-testid="airport-prev-page"
                  >
                    <ChevronLeft size={16} className="mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-[#0f172a] font-medium px-2">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || searching}
                    onClick={() => doSearch(page + 1)}
                    className="border-[#e2e8f0]"
                    data-testid="airport-next-page"
                  >
                    Next
                    <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {searchResults.length === 0 && !hasSearched && (
        <Card className="border-[#e2e8f0]">
          <CardContent className="py-12">
            <div className="text-center text-[#64748b]">
              <Database size={48} className="mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-[#0f172a] mb-2">Search for Airports</h3>
              <p>Enter an ICAO code or airport name above to search the database</p>
              <p className="text-sm mt-2">Database contains {totalCount.toLocaleString()} airport records</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Info */}
      <Card className="border-[#e2e8f0] mt-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#d4af37] rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin size={20} className="text-[#0f172a]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0f172a] mb-1">Auto-Recognition Feature</h3>
              <p className="text-sm text-[#64748b]">
                When creating a new flight, entering an ICAO code (Origin/Destination) from this database will automatically 
                fill in the Airport Name, Country, and Schengen status. Non-Schengen airports will be highlighted in Global Planning.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={!!editAirport} onOpenChange={() => setEditAirport(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0f172a] font-['Outfit']">
              {editAirport?.id === "new" ? "Add New Airport" : `Edit Airport - ${editForm.icao_code}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#0f172a]">ICAO Code *</Label>
              <Input
                value={editForm.icao_code}
                onChange={(e) => setEditForm({ ...editForm, icao_code: e.target.value.toUpperCase() })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., EBCI, LFPG"
                data-testid="edit-icao-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Airport Name</Label>
              <Input
                value={editForm.airport_name}
                onChange={(e) => setEditForm({ ...editForm, airport_name: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., Brussels South Charleroi"
                data-testid="edit-airport-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0f172a]">Country</Label>
              <Input
                value={editForm.country}
                onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                className="bg-white border-[#e2e8f0]"
                placeholder="e.g., Belgium"
                data-testid="edit-country-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-schengen"
                checked={editForm.schengen}
                onCheckedChange={(checked) => setEditForm({ ...editForm, schengen: checked })}
                data-testid="edit-schengen-checkbox"
              />
              <Label htmlFor="edit-schengen" className="cursor-pointer text-[#0f172a]">
                Schengen Area
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-non-eu"
                checked={editForm.non_eu}
                onCheckedChange={(checked) => setEditForm({ ...editForm, non_eu: checked })}
                data-testid="edit-non-eu-checkbox"
              />
              <Label htmlFor="edit-non-eu" className="cursor-pointer text-orange-600 font-medium">
                Non-EU
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditAirport(null)}
              className="border-[#e2e8f0]"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-airport-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Airport Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a]">Delete Airport</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this airport record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Dialog with Backup */}
      <DeleteWithBackupDialog
        open={deleteAllOpen}
        onOpenChange={setDeleteAllOpen}
        title="Delete All Airport Data"
        description="Are you sure you want to delete ALL airport records? This action cannot be undone."
        itemCount={totalCount}
        onConfirmDelete={handleDeleteAll}
        leavePath="/dashboard"
      />
    </div>
  );
}
